import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { SUPPORTED_LANG_CODES } from '@/application/i18n/language'
import type { LocaleAvailability } from '@/domains/types'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { TranslationStatusBadge } from './TranslationStatusBadge'

export interface TranslationCompletenessRow {
  id: string
  label: string
  locales: LocaleAvailability[]
  /** Makes the row's label a link (e.g. drilling down into a lead's own
   * facts). Omit for a row that has nowhere further to go (a fact, or the
   * entity's own "own fields" row). */
  linkTo?: { to: string; params: Record<string, string> }
  /** Rendered verbatim in the row's last column (e.g. an "Edit" action).
   * When no row in the table provides this, the whole actions column —
   * header included — is omitted. */
  actions?: ReactNode
  /** Highlights a row that represents the entity's own fields rather than
   * one of its children (e.g. "Investigation fields" above the lead rows). */
  emphasis?: boolean
}

interface TranslationCompletenessTableProps {
  labelHeader: string
  rows: TranslationCompletenessRow[]
  emptyMessage: string
  isLoading: boolean
}

/** Generalized version of the translation dashboard's table — one row per
 * entity, one column per supported locale, plus an optional label link and
 * an optional actions column. Pure presentation: no dialogs, no routing
 * decisions beyond rendering the `linkTo` it's given. */
export function TranslationCompletenessTable({
  labelHeader,
  rows,
  emptyMessage,
  isLoading,
}: TranslationCompletenessTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>
  }

  const hasActions = rows.some((tableRow) => tableRow.actions !== undefined)

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="py-2 pr-4">{labelHeader}</th>
            {SUPPORTED_LANG_CODES.map((locale) => (
              <th key={locale} className="py-2 pr-4">
                {locale.toUpperCase()}
              </th>
            ))}
            {hasActions && <th className="py-2 pr-4" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((tableRow) => (
            <tr
              key={tableRow.id}
              className={`border-b border-border/60 ${tableRow.emphasis ? 'font-semibold' : ''}`}
            >
              <td className="py-2 pr-4">
                {tableRow.linkTo ? (
                  <Link
                    to={tableRow.linkTo.to}
                    params={tableRow.linkTo.params}
                    className="text-primary hover:underline"
                  >
                    {tableRow.label}
                  </Link>
                ) : (
                  tableRow.label
                )}
              </td>
              {SUPPORTED_LANG_CODES.map((locale) => {
                const state = tableRow.locales.find((entry) => entry.locale === locale)
                return (
                  <td key={locale} className="py-2 pr-4">
                    <TranslationStatusBadge state={state} />
                  </td>
                )
              })}
              {hasActions && <td className="py-2 pr-4">{tableRow.actions}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
